import { type Option } from '@avanta/shared/components/ui/Select';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useFetchEnterpriseContracts } from 'hooks/useFetchEnterpriseContracts';
import { AppointmentsReservePayload } from 'pages/AppointmentsReserve/constants';
import { type AppointmentsReserveFormData } from 'pages/AppointmentsReserve/types';
import { type ContractRecordField } from 'types/enterprises';

interface UseGetContractOptionsResult {
  contractOptions: Option[];
  rateOptions: Option[];
  recordFields: ContractRecordField[];
  isLoading: boolean;
}

export const useGetContractOptions = (): UseGetContractOptionsResult => {
  const { control } = useFormContext<AppointmentsReserveFormData>();
  const [contractId, patient] = useWatch({
    control,
    name: [AppointmentsReservePayload.contractId, AppointmentsReservePayload.patient],
  });
  const patientId = patient?.id ?? '';
  const { data: contracts, isLoading } = useFetchEnterpriseContracts({ patientId });

  return useMemo(() => {
    const selectedContract = contracts?.find(contract => contract.id === Number(contractId));

    return {
      isLoading,
      contractOptions:
        contracts?.map(contract => ({
          value: String(contract.id),
          label: contract.title,
          description: contract.tenderNumber,
          ns: LOCIZE_NS.INSURANCE,
        })) ?? [],
      rateOptions:
        selectedContract?.rates?.map(rate => ({
          value: String(rate.id),
          label: rate.name,
          ns: LOCIZE_NS.INSURANCE,
        })) ?? [],
      recordFields: selectedContract?.recordFields ?? [],
    };
  }, [contracts, contractId, isLoading]);
};
